import * as R from 'ramda';
import { endOfYear, format } from 'date-fns';

import {
  getViolatedMandatoryRequirementsValidator,
  makeRequiredArray,
} from 'KnmErknmPackage/MainModule/common/violatedMandatoryRequirementsValidator';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import {
  INN_ERROR,
  MAX_CHARACTERS_ERROR,
  MIN_CHARACTERS_ERROR,
  REQUIRED_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeSnilsValidation } from 'CorePackage/MainModule/validators/isValidSnils';
import { makeIsErknmInn } from 'CorePackage/MainModule/validators/makeIsErknmInn';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { getSubjectValidate } from 'KnmPackage/KnmSubjectModule/common/getSubjectValidate';
import { IKnmFormSubject } from 'KnmPackage/KnmSubjectModule/interfaces/IKnmFormSubject';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { KnmFormValidator } from 'KnmPackage/MainModule/types/KnmFormValidator';
import { IKnmErknmFormSuspensionEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';
import { makeMaxNumber } from 'CorePackage/MainModule/validators/maxNumber';
import { makeMinNumber } from 'CorePackage/MainModule/validators/minNumber';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { isFileExtensionValid } from 'CorePackage/MainModule/validators/isFileExtensionValid';
import { pmExtensionDocumentValidFormats } from 'KnmPmPackage/PmExtensionModule/components/PmExtensionFileInput';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmErknmFormExpert } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormExpert';
import { IKnmErknmFormTakenMeasures } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormTakenMeasures';
import { getSubmittedDocumentsValidate } from 'KnmErknmPackage/MainModule/common/getSubmittedDocumentsValidate';
import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { getErknmAgreementValidate } from 'KnmErknmPackage/MainModule/common/getErknmAgreementValidate';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';
import { makeDateNotExcludedValidator } from 'CorePackage/MainModule/validators/isDateNotExcludedValidator';
import { makeNoSoonerExcludeValidator } from 'CorePackage/MainModule/validators/isNoSoonerExclude';
import { videoCallInfoValidator } from 'KnmErknmPackage/MainModule/common/videoCallInfoValidator';
import { mergeDeepRightWithArray } from 'CorePackage/MainModule/common/mergeDeepRightWithArray';
import { getArrayError } from 'CorePackage/MainModule/common/getArrayError';
import { validateInvalidFieldsPm } from 'KnmErknmPackage/MainModule/common/validateInvalidFieldsPm';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { makeMinCharactersValidator } from '../../../CorePackage/MainModule/validators/minCharacters';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmErknmFormApproveDoc } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormApproveDoc';
import { IKnmPmFormReason } from '../interfaces/IKnmPmFormReason';
import { IKnmPmFormRescheduling } from '../interfaces/IKnmPmFormRescheduling';
import { IKnmNotificationsForm } from '../../../KnmPackage/KnmNotificationModule/interfaces/IKnmNotificationsForm';
import { IKnmPmFormRequirementsNpa } from '../interfaces/IKnmPmFormRequirementsNpa';
import { makeInnChecksumVerification } from '../../../CorePackage/MainModule/validators/isInnCorrect';
import { isOneReasonLinkRequired } from '../../../KnmPackage/MainModule/common/isOneReasonLinkRequired';
import { CHECKING_IT_ACCREDITATION } from '../../../CorePackage/MainModule/constants/notices';
import { getRiskIndicatorsValidate } from '../../../KnmPackage/MainModule/common/getRiskIndicatorsValidate';
import { NOT_IN_FIAS_TEXT } from '../../../CorePackage/MainModule/constants/notInFIASText';
import { IKnmPmFormResultDecision } from '../interfaces/IKnmPmFormResultDecision';
import { getPmActValidate } from './getPmActValidate';
import {
  getKnmData,
  getKnmDataSigned,
  getKnmExcludeDates,
  getKnmInvalidFields,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { store } from '../../../CorePackage/MainModule/common/store';
import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';
import { getEventUavValidator } from '../../../KnmErknmPackage/MainModule/common/getEventUavValidate';
import { getKnmPmInitialFormValues } from '../selectors/knmPmSelectors';
import { cloneFormErrors } from '../../../CorePackage/MainModule/common/cloneFormErrors';
import { getSignersValidator } from '../../../KnmErknmPackage/MainModule/common/getSignersValidator';
import { getFamiliarizationNotificationsValidate } from '../../../KnmErknmPackage/MainModule/common/getFamiliarizationNotificationsValidate';
import { KindResultDecisionCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';

export const knmPmValidator = ({
  values,
  isNewKnm,
  isSupervisor,
  settings,
  knoSupervisionStartDate,
  isKnmStatusFillInProcess,
  isKnmStatusTypeWaitingCarryOut,
  isErknm,
}: KnmFormValidator & { values: IKnmForm }): FormErrors<IKnmForm> => {
  const {
    knoOrganizationPm,
    supervisionTypePm,
    pmType,
    startDate,
    stopDate,
    pmForm,
    location,
    reasonsPm,
    officialFullName,
    pmSignerPosition,
    signers,
    eventsPm,
    suspensionEvents,
    objectsPm,
    inspectorsErknm,
    experts,
    collaboratingOrganizationsErknm,
    isHasCollaboratingOrganization,
    requirementsNpa,
    approveDocs,
    takenMeasures,
    created,
    isViolatedMandatoryRequirements,
    violatedMandatoryRequirements,
    pmResultDecisions,
    isOrganizationNew,
    rafpNza,
    isUnableToRefuse,
    periodExtension,
    reasonRequirements,
    isPresentInItRegistry,
    notInFIAS: isNotInFIAS,
    matchesObjectAddress: isMatchesObjectAddress,
    region,
    manualAddress,
    isErrorFiasValue,
    rescheduling,
    subjectNotifications,
    abrogation,
    shouldValidationForGeneratedWarning,
    docTarget,
    act,
    startAndStopDateCountError: isStartAndStopDateCountError,
    interactionCategory,
    agreements,
    videoCallInfo,
  } = values;

  const state = store.getState();
  const excludeDates = getKnmExcludeDates(state);
  const isProsecutorOrSuperProsecutor = getUserRoleIsProsecutorOrSuperProsecutor(state);
  const knmRawData = getKnmData(state);
  const invalidFieldsFromBack = getKnmInvalidFields(state);

  const savedInvalidFields = sessionStorage.getItem('initialInvalidFields');
  let invalidFields;

  if (savedInvalidFields) {
    invalidFields = JSON.parse(savedInvalidFields);
  } else {
    invalidFields = invalidFieldsFromBack;
  }

  const saved = sessionStorage.getItem('initialValuesPm');
  let initialErknmValues;

  if (saved) {
    initialErknmValues = JSON.parse(saved);
  }

  const initialFormValues = initialErknmValues || getKnmPmInitialFormValues(state);
  const isTypePP = pmType?.code === ProfKindCodesEnum.PP;
  const isTypeOP = pmType?.code === ProfKindCodesEnum.OP;

  const isPmFormPreventiveConversation = pmForm?.id === 1;
  const reasonPmDateBegin = reasonsPm?.[0]?.type?.dateBegin;
  const reasonPmDateEnd = reasonsPm?.[0]?.type?.dateEnd;

  const isUseKnoSupervisorStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM,
  );

  const isValidateRequiredViolatedMandatory = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_43_STARTS_FROM,
  );

  const isLaterThanReasonFieldsAvailabilitySetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.REASON_FIELDS_AVAILABILITY_4_1_54_STARTS_FROM,
  );

  const isShowCheckingProgram = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_41_STARTS_FROM,
  );

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isLaterThanNpaValidationSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.NPA_VALIDATION_ERVK_ROT_4_1_24_STARTS_FROM,
  );

  const isValidatePmForm =
    isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_52_STARTS_FROM) &&
    (!isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.USE_REMOTE_MONITORING_PM_4_1_1_STARTS_FROM) ||
      !isErknm) &&
    !isNewKnm &&
    isSupervisor &&
    (!pmType || isTypePP);

  const isUse4123 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_23_STARTS_FROM);
  const isOn411 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.COLLABORATION_KNO_ERVK_SEARCH_4_1_1);
  const isUse4121 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_21_STARTS_FROM);

  const isLaterThanFieldsMarksSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.FIELDS_MARKS_481_4_1_14,
  );

  const isViolationsStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_29_STARTS_FROM,
  );

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  const isKnmReasons411Fields = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_1_STARTS_FROM,
  );
  const isLaterThanOrderInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_39_STARTS_FROM,
  );
  const isEnablePmLimitationOfSuspension = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.PM_LIMITATION_OF_SUSPENSION_STARTS_FROM,
  );

  const isLaterThen411 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.USE_REMOTE_MONITORING_PM_4_1_1_STARTS_FROM,
  );

  const isUseSigners = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ADD_NEW_SIGNER_199_4_1_6_STARTS_FROM,
  );

  const reasonsOrderStart = reasonsPm?.reduce((acc: string, item) => {
    if ((item?.order?.checkBeginDate && item?.order?.checkBeginDate > acc) || acc === undefined) {
      return item.order?.checkBeginDate;
    }

    return acc;
  }, undefined);

  const reasonsOrderEnd = reasonsPm?.reduce((acc: string, item) => {
    if ((item?.order?.checkEndDate && item?.order?.checkEndDate < acc) || acc === undefined) {
      return item.order?.checkEndDate;
    }

    return acc;
  }, undefined);

  const getEventsPmValidate = (): FormErrors<IKnmErknmFormEvent>[] | string[] | undefined => {
    return eventsPm?.map(item => ({
      ...getEventValidate(item),
    }));
  };

  const getEventValidate = (item: IKnmErknmFormEvent): FormErrors<IKnmErknmFormEvent> => {
    const newRescheduling = rescheduling?.find(i => i.isNew);
    const isSigned = getKnmDataSigned(state);

    return {
      type: makeRequired('Поле не может быть пустым')(item.type),
      interactionCategory: makeRequired('Поле не может быть пустым')(item.interactionCategory),
      startDate: composeValidators(
        makeRequired('Поле не может быть пустым'),
        // eslint-disable-next-line no-nested-ternary
        !newRescheduling
          ? rescheduling?.length
            ? () => undefined
            : makeNoLaterThenValidator(
                'Дата начала действия не должна быть позже даты окончания проведения ПМ',
                () => stopDate,
              )
          : makeNoLaterThenValidator('Введите новую дату действия', () => newRescheduling.newEndDate),
        // eslint-disable-next-line no-nested-ternary
        !newRescheduling
          ? rescheduling?.length
            ? () => undefined
            : makeNoSoonerThenValidator(
                'Дата начала действия не должна быть раньше даты начала проведения ПМ',
                () => startDate,
              )
          : makeNoSoonerThenValidator('Введите новую дату действия', () => newRescheduling.newStartDate),
      )(item.startDate),
      stopDate: composeValidators(
        makeRequired('Поле не может быть пустым'),
        makeNoSoonerThenValidator('Дата окончания не должна быть раньше даты начала;', () => item.startDate),
        // eslint-disable-next-line no-nested-ternary
        !newRescheduling
          ? rescheduling?.length
            ? () => undefined
            : makeNoLaterThenValidator(
                'Дата окончания действия не должна быть позже даты окончания проведения ПМ',
                () => stopDate,
              )
          : makeNoLaterThenValidator('Введите новую дату действия', () => newRescheduling.newEndDate),
        // eslint-disable-next-line no-nested-ternary
        !newRescheduling
          ? rescheduling?.length
            ? () => undefined
            : makeNoSoonerThenValidator(
                'Дата окончания действия не должна быть раньше даты начала проведения ПМ',
                () => startDate,
              )
          : makeNoSoonerThenValidator('Введите новую дату действия', () => newRescheduling.newStartDate),
      )(item.stopDate),
      submittedDocuments: !isEnablePaperlessDocument
        ? undefined
        : getSubmittedDocumentsValidate(item.submittedDocuments, item.type, isSigned, item.stopDate),
      uavTechnologies: item.isUavUsed === true ? getEventUavValidator(item.uavTechnologies) : undefined,
    };
  };

  const getPmSuspensionEventsValidate = (): FormErrors<IKnmErknmFormSuspensionEvent>[] | undefined => {
    const getLimitationOfSuspensionDate = () => {
      // Валидация только для обязательного ПМ
      if (!stopDate || !isUnableToRefuse) return undefined;
      const pmStopYear = parseDate({ date: stopDate, parseFormat: DATE_FORMAT_LONG_FNS });

      return pmStopYear ? format(endOfYear(pmStopYear), DATE_FORMAT_LONG_FNS) : undefined;
    };

    return suspensionEvents?.map(item => {
      let pmEvent: IKnmErknmFormEvent | undefined;

      if (item.event?.type?.label) {
        pmEvent = eventsPm?.find(i => i.type?.title === item.event?.type?.label);
      }

      return {
        attachments: makeRequired(REQUIRED_ERROR)(item.attachments),
        event: makeRequired(REQUIRED_ERROR)(item.event),
        decisionDateTime: makeRequired(REQUIRED_ERROR)(item.decisionDateTime),
        // decisionNumber: makeRequired(REQUIRED_ERROR)(item.decisionNumber),
        startDate: composeValidators(
          makeRequired(REQUIRED_ERROR),
          makeNoSoonerThenValidator('Дата начала должна быть больше или равна дате начала ПМ', () => startDate),
          makeNoLaterThenValidator('Дата начала должна быть меньше или равна дате окончания ', () => item.stopDate),
          makeNoLaterThenValidator('Дата начала должна быть меньше или равна дате окончания ПМ', () => stopDate),
          // добавлена валидация для ПМ чтобы дата начала приостановки была больше или равная дате начала действия
          makeNoSoonerThenValidator(
            'Дата начала должна быть больше или равна дате начала действия',
            () => pmEvent?.startDate,
          ),
          // добавлена валидация для ПМ чтобы дата начала приостановки не выходила за дату окончания действия
          makeNoLaterThenValidator(
            'Дата начала должна быть меньше или равна дате окончания действия',
            () => pmEvent?.stopDate,
          ),
        )(item.startDate),
        stopDate: composeValidators(
          makeRequired(REQUIRED_ERROR),
          makeNoSoonerThenValidator('Дата окончания должна быть больше или равна дате начала ПМ', () => startDate),
          makeNoSoonerThenValidator('Дата окончания должна быть больше или равна дате начала', () => item.startDate),
          isEnablePmLimitationOfSuspension
            ? makeNoLaterThenValidator(
                'Приостановление срока проведения обязательного профилактического визита возможно только в пределах календарного года проведения мероприятия',
                getLimitationOfSuspensionDate,
              )
            : () => undefined,
        )(item.stopDate),
      };
    });
  };

  const getReasonsPmValidate = (): FormErrors<IKnmPmFormReason>[] | undefined => {
    return reasonsPm?.map(item => {
      const getOrderValidate = (value: unknown) =>
        isAssignmentRequiredByType(item.type) && isLaterThanOrderInProd
          ? makeRequired('Поле не может быть пустым')(value)
          : undefined;

      return {
        type: makeRequired('Поле не может быть пустым')(item.type),
        text: item.type?.hasText && !isNewKnm ? makeRequired('Поле не может быть пустым')(item.text) : undefined,
        date:
          !item.type?.assignmentNumberRequired && !item.type?.approveRequired && item.type?.dateRequired && !isNewKnm
            ? makeRequired('Поле не может быть пустым')(item.date)
            : undefined,
        riskIndicators:
          item.type?.indicatorRiskRequired && !isNewKnm
            ? getRiskIndicatorsValidate(item.riskIndicators, item.type?.indicatorRiskRequiredFile)
            : undefined,
        requirementDetails:
          isKnmReasons411Fields && (item.type?.requirementDetailsRequired || item.type?.detailsRequirement) && !isNewKnm
            ? makeRequired('Поле не может быть пустым')(item.requirementDetails)
            : undefined,
        dateRequirement:
          item.type?.dateRequirement && !isNewKnm
            ? makeRequired('Поле не может быть пустым')(item.dateRequirement)
            : undefined,
        order: !isNewKnm ? getOrderValidate(item.order) : undefined,
        assignmentDate: item.order && !isNewKnm ? makeRequired(REQUIRED_ERROR)(item.assignmentDate) : undefined,
        assignmentNumber: item.order && !isNewKnm ? makeRequired(REQUIRED_ERROR)(item.assignmentNumber) : undefined,
        fullName: item.order && !isNewKnm ? makeRequired(REQUIRED_ERROR)(item.fullName) : undefined,
        links:
          isLaterThanReasonFieldsAvailabilitySetting && isOneReasonLinkRequired(item.type) && !isNewKnm
            ? makeRequired(REQUIRED_ERROR)(item.links)
            : undefined,
        linkValidation:
          isLaterThanReasonFieldsAvailabilitySetting && isOneReasonLinkRequired(item.type) && !isNewKnm
            ? composeValidators(
                item.links?.some(link => link.linkValidationMessage)
                  ? () => item.links?.find(link => link.linkValidationMessage)?.linkValidationMessage
                  : () => undefined,
              )(item.links)
            : undefined,
        programNumber:
          isShowCheckingProgram &&
          item?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM &&
          item.type?.programNumberRequired &&
          !isNewKnm
            ? makeRequired('Поле не может быть пустым')(item.programNumber)
            : undefined,
        programAttachment:
          isShowCheckingProgram &&
          item?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM &&
          item.type?.programFileRequired &&
          !isNewKnm
            ? makeRequired('Поле не может быть пустым')(item.programAttachment)
            : undefined,
      };
    });
  };

  const getSubjectPmValidate = (subjectPmItem?: IKnmFormSubject) => {
    const { inn, isFiz, organizationName, snils, document, country, subjectTypePm } = subjectPmItem ?? {};

    const isSubjectFIZ = subjectTypePm?.label === KnmSubjectTypesEnum.FIZ;
    const isSubjectUR = subjectTypePm?.label === KnmSubjectTypesEnum.UR;
    const isSubjectIFIZ = subjectTypePm?.label === KnmSubjectTypesEnum.IFIZ;
    const isSubjectIUR = subjectTypePm?.label === KnmSubjectTypesEnum.IUR;
    const isSubjectFIUR = subjectTypePm?.label === KnmSubjectTypesEnum.FIUR;
    const getIsObjectsValue = () => !!(objectsPm?.length && pmType?.code === ProfKindCodesEnum.OP);

    /*
    По 604 постановлению:
    - Объявление предостережения
      КЛ - может быть не заполнен, если заполнен объект
      ОБК - может быть не заполнен, если заполнен субъект
    - Профилактический визит
        КЛ и ОБК обязательны
     */
    if (isOrganizationNew === undefined) {
      return {
        isOrganizationNew:
          (isNewKnm || isKnmStatusFillInProcess) && (isTypeOP ? !objectsPm?.length : isTypePP)
            ? REQUIRED_ERROR
            : undefined,
      };
    }

    const isInnException =
      getIsObjectsValue() || !!snils || isSubjectIFIZ || isSubjectIUR || (isSubjectFIUR && !!rafpNza);

    return {
      ...getSubjectValidate(subjectPmItem),
      subjectTypePm: makeRequired(REQUIRED_ERROR)(subjectTypePm),
      inn: composeValidators(
        makeRequired(REQUIRED_ERROR, () => isInnException),
        makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
        makeIsErknmInn({
          errorMessage: INN_ERROR(),
          getIsFiz: () => isFiz,
          getException: () => isInnException,
        }),
        inn ? makeInnChecksumVerification() : () => undefined,
      )(inn),
      snils: composeValidators(
        makeRequired(REQUIRED_ERROR, () => getIsObjectsValue() || !!inn || !isSubjectFIZ),
        makeSnilsValidation('Поле заполнено некорректно', () => !snils),
      )(snils),
      organizationName: composeValidators(
        makeRequired(REQUIRED_ERROR, () => getIsObjectsValue() || !(isSubjectFIZ || isSubjectIFIZ || isSubjectIUR)),
        makeMinCharactersValidator(3, MIN_CHARACTERS_ERROR(3), () => !isSubjectFIZ),
        makeMaxCharactersValidator(1017, MAX_CHARACTERS_ERROR, () => !(isSubjectFIZ || isSubjectUR || isSubjectIUR)),
        makeMaxCharactersValidator(1018, MAX_CHARACTERS_ERROR, () => !isSubjectIFIZ),
      )(organizationName),
      document: composeValidators(
        makeRequired(REQUIRED_ERROR, () => getIsObjectsValue() || !isSubjectIFIZ),
        makeMaxCharactersValidator(1000, MAX_CHARACTERS_ERROR, () => !isSubjectIFIZ),
      )(document),
      country: makeRequired(REQUIRED_ERROR, () => getIsObjectsValue() || !(isSubjectIUR || isSubjectIFIZ))(country),
    };
  };

  const getCollaboratingOrganizationsValidate = () => {
    if (!isHasCollaboratingOrganization || !isLaterThanFieldsMarksSetting) {
      return undefined;
    }

    if (!collaboratingOrganizationsErknm?.length) {
      return REQUIRED_ERROR;
    }

    return collaboratingOrganizationsErknm.map(item => ({
      organization: !isOn411 ? makeRequired(REQUIRED_ERROR)(item.organization) : undefined,
      title: isOn411 ? makeRequired(REQUIRED_ERROR)(item.title) : undefined,
    }));
  };

  const getPmResultDecisionsValidate = (): FormErrors<IKnmPmFormResultDecision>[] | undefined => {
    // новые обязательные поля добавить в PmResultOfInspection/common/isResultDecisionFilled.ts
    return pmResultDecisions?.map(item => ({
      kindDecision: item.paperless ? makeRequired(REQUIRED_ERROR)(item.kindDecision) : undefined,
      number: item.paperless ? undefined : makeRequired(REQUIRED_ERROR)(item.number),
      date: item.paperless ? undefined : makeRequired(REQUIRED_ERROR)(item.date),
      attachment: item.paperless ? undefined : makeRequired(REQUIRED_ERROR)(item.attachment),
      signerFullName:
        (isTypePP && item.systemSignatureUsed) ||
        (isUseSigners &&
          (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
            item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1))
          ? undefined
          : makeRequired(REQUIRED_ERROR)(item.signerFullName),
      signerPosition:
        (isTypePP && item.systemSignatureUsed) ||
        (isUseSigners &&
          (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
            item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1))
          ? undefined
          : makeRequired(REQUIRED_ERROR)(item.signerPosition),
      signers:
        isTypePP &&
        isUseSigners &&
        isErknm &&
        (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
          item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1)
          ? getSignersValidator(item.signers)
          : undefined,
      deadline: item.paperless ? undefined : makeRequired(REQUIRED_ERROR)(item.deadline),
      information: !item?.violatedMandatoryRequirements?.length
        ? makeRequired(REQUIRED_ERROR)(item.information)
        : undefined,
      informationReactQuillEditor: !item?.violatedMandatoryRequirements?.length
        ? makeRequiredQuillEditorDelta(REQUIRED_ERROR)(item.informationReactQuillEditor)
        : undefined,
      founder:
        item.subjectType?.id === KnmResultDecisionSubjectTypesEnum.FOUNDER
          ? getSubjectValidate(item.founder)
          : undefined,
      violatedMandatoryRequirements: composeValidators(
        makeRequiredArray(REQUIRED_ERROR),
        getViolatedMandatoryRequirementsValidator({
          isSigned: item.paperless ? item.signed : undefined,
          isNeedViolationPLanFixValidate: true,
          isValidateRequiredViolatedMandatory,
          isDecisionMode: true,
          isProdReady: isViolationsStartDate,
          isPaperless: item.paperless,
          isDecision: false,
          isActMode: false,
          isViolationEventsRequired: false,
        }),
      )(item.violatedMandatoryRequirements),
      familiarizationNotifications: getFamiliarizationNotificationsValidate({
        familiarizationNotifications: item?.familiarizationNotifications,
        signers: item?.signers,
        firstExtractDateTime: item?.firstExtractDateTime,
        isUseSigners:
          isTypePP &&
          !!isErknm &&
          isUseSigners &&
          (item?.kindDecision?.code === KindResultDecisionCodesEnum.D_7 ||
            item?.kindDecision?.code === KindResultDecisionCodesEnum.D_1),
      }),
    }));
  };

  const getObjectsPmValidate = (): FormErrors<IKnmErknmFormObject>[] | undefined => {
    return objectsPm?.map(item => ({
      name: item.ervkId ? makeRequired('Поле не может быть пустым')(item.name) : undefined,
      address: composeValidators(
        isUse4123 && item.isErrorFiasValue && !item.notInFIAS ? () => NOT_IN_FIAS_TEXT : () => undefined,
        item.notInFIAS ? () => undefined : makeRequired('Поле не может быть пустым'),
      )(item.address),
      objectType: makeRequired('Поле не может быть пустым')(item.objectType),
      objectKind: makeRequired('Поле не может быть пустым')(item.objectKind),
      objectSubKind: makeRequired('Поле не может быть пустым')(item.objectSubKind),
      region: item.notInFIAS && !item.ervkId ? makeRequired('Поле не может быть пустым')(item.region) : undefined,
      // eslint-disable-next-line no-nested-ternary
      manualAddress: item.notInFIAS ? makeRequired('Поле не может быть пустым')(item.manualAddress) : undefined,
    }));
  };

  const getInspectorsErknmValidate = (): FormErrors<IKnmErknmFormInspector>[] | string | undefined => {
    return inspectorsErknm?.map(item => {
      return {
        fullName: makeRequired('Поле не может быть пустым')(item.fullName),
        position: makeRequired('Поле не может быть пустым')(item.position),
      };
    });
  };

  const requirementsNpaDuplicate = (requirementsNpaItem: IKnmPmFormRequirementsNpa) => {
    let requirementsNpaIds: string[] = [];

    requirementsNpa
      ?.map(item => item.mandatory?.map(m => `${item.npa.id}/${item.structuralUnit.id}/${m.id}`))
      .forEach(item => {
        requirementsNpaIds = requirementsNpaIds.concat(item);
      });

    let requirementsNpaItemIds: string[] = [];

    requirementsNpaItem.mandatory
      ?.map(m => `${requirementsNpaItem.npa.id}/${requirementsNpaItem.structuralUnit.id}/${m.id}`)
      .forEach(item => {
        requirementsNpaItemIds = requirementsNpaItemIds.concat(item);
      });

    return requirementsNpaItemIds.map(item => ({
      id:
        requirementsNpaIds.filter(id => id === item).length > 1
          ? 'Поле должно содержать уникальное значение'
          : undefined,
    }));
  };

  const getRequirementsNpaValidate = (): FormErrors<IKnmPmFormRequirementsNpa>[] | string | undefined => {
    if (shouldValidationForGeneratedWarning) {
      return makeRequired('Поле не может быть пустым')(requirementsNpa);
    }

    let hasCorrectControlObjectFilter = true;
    let hasCorrectRiscIndicatorsFilter = true;

    requirementsNpa?.forEach(item => {
      if (item?.mandatory?.find(m => m?.hasInvalidControlObjectFilter === true)) {
        hasCorrectControlObjectFilter = false;
      }
      if (item?.mandatory?.find(m => m?.hasInvalidRiscIndicatorsFilter === true)) {
        hasCorrectRiscIndicatorsFilter = false;
      }
    });

    return requirementsNpa?.map(item => {
      const mandatoryRequiredError = makeRequired('Поле не может быть пустым')(item.mandatory);

      return {
        npa: isLaterThanNpaValidationSetting
          ? makeRequired('')(hasCorrectControlObjectFilter && hasCorrectRiscIndicatorsFilter)
          : undefined,
        mandatory: mandatoryRequiredError ? getArrayError(mandatoryRequiredError) : requirementsNpaDuplicate(item),
      };
    });
  };

  const getApproveDocsValidate = (): FormErrors<IKnmErknmFormApproveDoc>[] | undefined => {
    return approveDocs?.map(item => ({
      approveDoc: makeRequired('Поле не может быть пустым')(item.approveDoc),
    }));
  };

  const getTakenMeasuresValidate = (): FormErrors<IKnmErknmFormTakenMeasures>[] | FormErrors<String> | undefined => {
    if (!isEnablePaperlessDocument || !isTypeOP) {
      return undefined;
    }

    if (!takenMeasures || !takenMeasures?.length) {
      return makeRequired(REQUIRED_ERROR)(takenMeasures);
    }

    const itemsValidation = takenMeasures.map(tm => {
      const valueError = makeRequired(REQUIRED_ERROR)(tm.value);

      return valueError ? { value: valueError } : undefined;
    });

    return itemsValidation.some(Boolean) ? itemsValidation : undefined;
  };

  const getStartDateValidate = () =>
    composeValidators(
      makeNoLaterThenValidator('Дата начала должна быть меньше или равна дате окончания', () => stopDate),
      reasonsOrderStart && !suspensionEvents?.length && (!isTypePP || (isTypePP && !periodExtension))
        ? makeNoSoonerThenValidator(
            'Дата начала должна быть больше или равна дате начала поручения',
            () => reasonsOrderStart,
          )
        : () => undefined,
      reasonsOrderEnd && !suspensionEvents?.length && (!isTypePP || (isTypePP && !periodExtension))
        ? makeNoLaterThenValidator(
            'Дата начала должна быть меньше или равна дате окончания поручения',
            () => reasonsOrderEnd,
          )
        : () => undefined,
      makeRequired('Поле не может быть пустым'),
      isNewKnm
        ? composeValidators(
            makeNoSoonerExcludeValidator(
              'Дата начала не может быть меньше минимально доступной даты',
              () => excludeDates?.startDate?.minDate,
            ),
            makeDateNotExcludedValidator(
              'Дата начала не может быть из списка исключенных дат',
              () => excludeDates?.startDate?.excludeDates,
            ),
          )
        : () => undefined,
    )(startDate) ||
    (isNewKnm && isUseKnoSupervisorStartDate
      ? composeValidators(
          makeRequired('Отсутствуют сведения о дате начала действия вида контроля. Необходимо внести сведения в ЕРВК.'),
          makeNoLaterThenValidator(
            'Выбранная дата раньше начала вступления в силу положения о виде контроля (надзора)',
            () => startDate,
          ),
        )(knoSupervisionStartDate)
      : undefined) ||
    validateIsDateBetweenReasonPmDates(startDate);

  const getStopDateValidate = () =>
    stopDate
      ? composeValidators(
          reasonsOrderStart && !suspensionEvents?.length && (!isTypePP || (isTypePP && !periodExtension))
            ? makeNoSoonerThenValidator(
                'Дата окончания должна быть больше или равна дате начала поручения',
                () => reasonsOrderStart,
              )
            : () => undefined,
          reasonsOrderEnd && !suspensionEvents?.length && (!isTypePP || (isTypePP && !periodExtension))
            ? makeNoLaterThenValidator(
                'Дата окончания должна быть меньше или равна дате окончания поручения',
                () => reasonsOrderEnd,
              )
            : () => undefined,
          makeNoSoonerThenValidator('Дата окончания должна быть больше или равна дате начала', () => startDate),
          isNewKnm
            ? composeValidators(
                makeNoSoonerExcludeValidator(
                  'Дата окончания не может быть меньше минимально доступной даты',
                  () => excludeDates?.stopDate?.minDate,
                ),
              )
            : () => undefined,
          isStartAndStopDateCountError
            ? () => 'Продолжительность обязательного профилактического визита не должна превышать 10 рабочих дней'
            : () => undefined,
        )(stopDate) || validateIsDateBetweenReasonPmDates(stopDate)
      : undefined;

  const getPeriodExtensionValidate = (): FormErrors<IKnmPmFormExtension> => {
    const isReasonWithAssignment = reasonsPm?.some(reason => isAssignmentRequiredByType(reason.type));
    const fileFormatValidator = (attachment: IKnmFormAttachment | undefined) =>
      isFileExtensionValid(attachment?.fileName || '', pmExtensionDocumentValidFormats)
        ? undefined
        : 'Неверный формат файла';

    // Todo использовать settings для ограничений amountDays

    return {
      attachment: composeValidators(makeRequired(REQUIRED_ERROR), fileFormatValidator)(periodExtension?.attachment),
      decisionDateTime: makeRequired(REQUIRED_ERROR)(periodExtension?.decisionDateTime),
      decisionNumber: makeRequired(REQUIRED_ERROR)(periodExtension?.decisionNumber),
      amountDays: composeValidators(
        makeRequired(REQUIRED_ERROR),
        makeIsNormalInteger('Должно быть положительным целым числом'),
        makeMinNumber(1, 'Срок продления не может быть меньше 1'),
        makeMaxNumber(
          isReasonWithAssignment ? 4 : 3,
          `Срок продления не должен превышать ${isReasonWithAssignment ? 4 : 3} дня`,
        ),
      )(periodExtension?.amountDays),
    };
  };

  const validateIsDateBetweenReasonPmDates = (date: string | undefined): string | undefined => {
    if (!date) {
      return;
    }

    return composeValidators(
      (isNewKnm || rescheduling?.length) && reasonPmDateBegin
        ? makeNoSoonerThenValidator('ПМ должно проводиться в период действия основания', () =>
            formatDate({
              date: reasonPmDateBegin,
              dateFormat: DATE_FORMAT_LONG_FNS,
              parseFormat: SYSTEM_DATE_FORMAT_FNS,
            }),
          )
        : () => undefined,
      (isNewKnm || rescheduling?.length) && reasonPmDateEnd
        ? makeNoLaterThenValidator('ПМ должно проводиться в период действия основания', () =>
            formatDate({
              date: reasonPmDateEnd,
              dateFormat: DATE_FORMAT_LONG_FNS,
              parseFormat: SYSTEM_DATE_FORMAT_FNS,
            }),
          )
        : () => undefined,
    )(date);
  };

  const getSubjectNotificationsValidate = (): FormErrors<IKnmNotificationsForm>[] | undefined => {
    const newRescheduling = rescheduling?.find(i => i.isNew);
    const hasNewSubjectNotification = !!subjectNotifications?.find(i => i.isNew);

    return subjectNotifications?.map(item => {
      if (item.isNew) {
        return { date: undefined };
      }

      return {
        date:
          !newRescheduling || hasNewSubjectNotification
            ? undefined
            : composeValidators(
                makeNoLaterThenValidator('Внесите новые сведения об уведомлении', () => newRescheduling.newStartDate),
                newRescheduling.newEndDate
                  ? makeNoSoonerThenValidator('Внесите новые сведения об уведомлении', () => newRescheduling.newEndDate)
                  : () => undefined,
              )(item.date),
      };
    });
  };

  const getReschedulingValidate = (): FormErrors<IKnmPmFormRescheduling>[] | undefined => {
    return rescheduling?.map(item => ({
      newStartDate: !item.isNew
        ? undefined
        : composeValidators(
            makeNoLaterThenValidator(
              'Новая дата начала должна быть меньше или равна новой дате окончания',
              () => item?.newEndDate,
            ),
            makeRequired('Поле не может быть пустым'),
          )(item?.newStartDate),
      newEndDate: !isTypePP
        ? undefined
        : composeValidators(
            makeNoSoonerThenValidator(
              'Новая дата окончания должна быть больше или равна дате начала',
              () => item?.newStartDate,
            ),
            makeRequired('Поле не может быть пустым'),
          )(item?.newEndDate),
    }));
  };

  const getAbrogationValidate = (): { [indesx: string]: string } | undefined => {
    if (abrogation === undefined || abrogation === null) {
      return undefined;
    }

    const obj: { [indesx: string]: string } = {};

    if (!abrogation?.reasonId?.id) {
      obj.reasonId = 'Поле не может быть пустым';
    }

    return obj;
  };

  const getExpertsValidate = (): FormErrors<IKnmErknmFormExpert>[] | undefined => {
    return experts?.map(item => ({
      title: makeRequired('Поле не может быть пустым')(item.title),
      type: makeRequired('Поле не может быть пустым')(item.type),
    }));
  };

  const isVideoCallInfoValidate = isShow22 && interactionCategory && interactionCategory.id !== 3 && !isNewKnm;

  const isInteractionCategoryValidate =
    isLaterThen411 && isKnmStatusFillInProcess && pmType?.code === ProfKindCodesEnum.PP && isErknm;

  const resultValidateObj: FormErrors<IKnmForm> = {
    knoOrganizationPm: makeRequired('Поле не может быть пустым')(knoOrganizationPm),
    supervisionTypePm: makeRequired('Поле не может быть пустым')(supervisionTypePm),
    pmType: makeRequired('Поле не может быть пустым')(pmType),
    region: isNotInFIAS && !isMatchesObjectAddress ? makeRequired('Поле не может быть пустым')(region) : undefined,
    // eslint-disable-next-line no-nested-ternary
    manualAddress: isNotInFIAS
      ? makeRequired('Поле не может быть пустым')(manualAddress)
      : isMatchesObjectAddress && !location
      ? makeRequired('Выберите адрес')(manualAddress)
      : undefined,
    startDate: getStartDateValidate(),
    stopDate: getStopDateValidate(),
    pmForm: isValidatePmForm ? makeRequired('Поле не может быть пустым')(pmForm) : undefined,
    // делаем поля необязательными если паспорт подписан в автоматическом режиме
    officialFullName:
      isUse4121 && isTypePP && !isNewKnm && !knmRawData?.pmErknm?.preventiveVisitSystemSignatureUsed && !isUseSigners
        ? makeRequired('Поле не может быть пустым')(officialFullName)
        : undefined,
    pmSignerPosition:
      isUse4121 && isTypePP && !isNewKnm && !knmRawData?.pmErknm?.preventiveVisitSystemSignatureUsed && !isUseSigners
        ? makeRequired('Поле не может быть пустым')(pmSignerPosition)
        : undefined,
    signers: isUseSigners && isErknm && isTypePP && !isNewKnm ? getSignersValidator(signers) : undefined,
    location: composeValidators(
      isTypePP && isUse4123 && isErrorFiasValue && !isNotInFIAS ? () => NOT_IN_FIAS_TEXT : () => undefined,
      // eslint-disable-next-line no-nested-ternary
      isNotInFIAS
        ? () => undefined
        : // eslint-disable-next-line no-nested-ternary
        isMatchesObjectAddress && !manualAddress && !location
        ? makeRequired('Поле не может быть пустым')
        : isPmFormPreventiveConversation && !manualAddress && !location
        ? makeRequired('Поле не может быть пустым')
        : () => undefined,
      location ? makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки') : () => undefined,
    )(location),
    reasonsPm: reasonsPm?.length ? getReasonsPmValidate() : getArrayError('Поле не может быть пустым'),
    inspectorsErknm: !isNewKnm ? getInspectorsErknmValidate() : undefined,
    experts: experts && experts.length ? getExpertsValidate() : undefined,
    eventsPm: !isNewKnm ? getEventsPmValidate() : undefined,
    suspensionEvents: getPmSuspensionEventsValidate(),
    collaboratingOrganizationsErknm: getCollaboratingOrganizationsValidate(),
    requirementsNpa: isKnmStatusFillInProcess && docTarget?.length ? undefined : getRequirementsNpaValidate(),
    approveDocs: approveDocs?.length ? getApproveDocsValidate() : undefined,
    takenMeasures: !isNewKnm ? getTakenMeasuresValidate() : undefined,
    isPresentInItRegistry:
      reasonRequirements?.it !== null &&
      ((reasonRequirements?.it === true && isPresentInItRegistry === false) ||
        (reasonRequirements?.it === false && isPresentInItRegistry === true))
        ? CHECKING_IT_ACCREDITATION
        : undefined,
    rescheduling: getReschedulingValidate(),
    subjectNotifications: getSubjectNotificationsValidate(),
    abrogation: getAbrogationValidate(),
    pmResultDecisions: getPmResultDecisionsValidate(),
    act: getPmActValidate({
      act,
      inspectorsErknm,
      isInteractionCategoryOrInspectorRequired: true,
      isProsecutorOrSuperProsecutor,
      isValidateRequiredViolatedMandatory,
      isViolationsStartDate,
      rescheduling,
      startDate,
      stopDate,
      isUnableToRefuse,
      eventsErknm: eventsPm,
      suspensionEvents,
      isUseSigners: isUseSigners && !!isErknm,
    }),
    objectsPm: (() => {
      if (isNewKnm) {
        return undefined;
      }

      const isObjectsPmRequired =
        (isTypePP || (isTypeOP && !!getSubjectPmValidate(values)?.isOrganizationNew)) &&
        (isKnmStatusFillInProcess || isNewKnm);

      if (isObjectsPmRequired && !objectsPm?.length) {
        return getArrayError('Поле не может быть пустым');
      }

      return getObjectsPmValidate();
    })(),
    ...(isNewKnm && Boolean(values.pmType || values.reasonsPm?.length || values.takenMeasures)
      ? {}
      : getSubjectPmValidate(values)),
    isViolatedMandatoryRequirements:
      !isEnablePaperlessDocument && !act?.paperless && isKnmStatusTypeWaitingCarryOut && isUnableToRefuse
        ? makeRequired('Поле не может быть пустым')(isViolatedMandatoryRequirements)
        : undefined,
    violatedMandatoryRequirements:
      isViolatedMandatoryRequirements?.value === true
        ? composeValidators(
            makeRequiredArray('Поле не может быть пустым'),
            getViolatedMandatoryRequirementsValidator({
              isSigned: undefined,
              isNeedViolationPLanFixValidate: true,
              isValidateRequiredViolatedMandatory,
              isDecisionMode: false,
              isProdReady: isViolationsStartDate,
              isDecision: false,
              isActMode: false,
              isViolationEventsRequired: false,
            }),
          )(violatedMandatoryRequirements)
        : undefined,
    periodExtension: periodExtension && !isEnablePmLimitationOfSuspension ? getPeriodExtensionValidate() : undefined,
    interactionCategory: isInteractionCategoryValidate ? makeRequired(REQUIRED_ERROR)(interactionCategory) : undefined,
    videoCallInfo: isVideoCallInfoValidate
      ? composeValidators(
          makeRequired('Поле не может быть пустым'),
          videoCallInfoValidator(rescheduling, startDate, stopDate),
        )(videoCallInfo)
      : () => undefined,
    agreements: agreements?.map(agreement =>
      getErknmAgreementValidate({
        agreement,
        isPaperless: !!pmResultDecisions?.find(i => i.guid === agreement?.guid)?.paperless,
        isSupervisor: !!isSupervisor,
      }),
    ),
  };

  const invalidFieldsValidate: FormErrors<IKnmForm> = validateInvalidFieldsPm(
    values,
    initialFormValues,
    isNewKnm,
    invalidFields,
  );

  const resultValidate = cloneFormErrors(mergeDeepRightWithArray(resultValidateObj, invalidFieldsValidate));

  if (resultValidate['agreements' as keyof IKnmForm]) {
    resultValidate['agreements' as keyof IKnmForm].forEach((agreement: IErknmFormAgreement, index: number) => {
      if (Array.isArray(agreement?.terminate?.terminationReasons)) {
        let isReasonsEmpty = true;

        agreement?.terminate?.terminationReasons?.forEach(reason => {
          if (!R.isNil(reason)) {
            isReasonsEmpty = false;
          }
        });

        if (agreement?.terminate && isReasonsEmpty) {
          resultValidate['agreements' as keyof IKnmForm][index].terminate.terminationReasons = undefined;
        }
      }

      if (Array.isArray(agreement?.changes)) {
        agreement?.changes?.forEach((change, changeIndex) => {
          let isReasonsEmpty = true;

          change.types?.forEach(type => {
            if (!R.isNil(type)) {
              isReasonsEmpty = false;
            }
          });

          if (isReasonsEmpty) {
            resultValidate['agreements' as keyof IKnmForm][index].changes[changeIndex].types = undefined;
          }

          isReasonsEmpty = true;
          change.reasons?.forEach(reason => {
            if (!R.isNil(reason)) {
              isReasonsEmpty = false;
            }
          });

          if (isReasonsEmpty) {
            resultValidate['agreements' as keyof IKnmForm][index].changes[changeIndex].reasons = undefined;
          }
        });
      }
    });
  }

  if (typeof resultValidate['collaboratingOrganizationsErknm' as keyof IKnmForm] === 'string') {
    resultValidate['collaboratingOrganizationsErknm' as keyof IKnmForm] = getArrayError(
      resultValidate['collaboratingOrganizationsErknm' as keyof IKnmForm],
    );
  }

  return resultValidate;
};
